import type { Participant } from '@seen/core/models/patients/participant'
import {
  getFilenameFromContentDisposition,
  saveBlobWithName,
} from '@seen/core/utils'
import { IconFileExport } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { JSX } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import {
  DisenrollDropdownMenuItem,
  WithdrawEnrollmentDropdownMenuItem,
} from '#src/components/patient/detail-content/contact-tab/DisenrollmentDate.tsx'
import { PatientCard } from '#src/components/patient/PatientCard.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useShowMedplumLink } from '#src/hooks/useShowMedplumLink.ts'
import { toast } from '#src/utils/toast.ts'

export interface PatientMoreMenuProps {
  participant: Participant
  className?: string
}

export function PatientMoreMenu({
  participant,
  className,
}: PatientMoreMenuProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const showMedplumLink = useShowMedplumLink()
  const patchParticipant = usePatchMutation<Participant>().mutate
  const queryClient = useQueryClient()
  const seenApi = useSeenApi().seenApi

  const exportFacesheetMutation = useMutation({
    mutationFn: async () => {
      const response = await seenApi.post(
        `/pdf/facesheet/${participant.id}`,
        {},
        { responseType: 'blob' }
      )
      const filename = getFilenameFromContentDisposition(
        response.headers['content-disposition']
      )
      saveBlobWithName(response.data, filename ?? 'facesheet.pdf')
    },
    onMutate: () => {
      toast.loading('Exporting facesheet…', { id: 'export-facesheet' })
    },
    onSettled: () => {
      toast.dismiss('export-facesheet')
    },
    onError: () => {
      toast.error('Failed to export facesheet')
    },
  })

  return (
    <MoreDropdownMenu className={className}>
      <DropdownMenuItem
        onClick={() => exportFacesheetMutation.mutate()}
        disabled={exportFacesheetMutation.isPending}
      >
        <IconFileExport size={14} />
        {exportFacesheetMutation.isPending ? 'Exporting…' : 'Export Facesheet'}
      </DropdownMenuItem>
      {showMedplumLink && <DropdownMenuMedplumLinkItem to={participant} />}
      {!isReadOnly && (
        <>
          <WithdrawEnrollmentDropdownMenuItem participant={participant} />
          <DisenrollDropdownMenuItem participant={participant} />
          <DropdownMenuDeleteItem
            deleteConfirmationProps={{
              title: `Delete participant?`,
              description: (
                <div className="flex flex-col gap-4 select-none">
                  <Card>
                    <PatientCard participant={participant} />
                  </Card>
                  <span>
                    Deleted participants can still be recovered by contacting
                    our team.
                  </span>
                </div>
              ),
              onConfirm: () => {
                patchParticipant(
                  {
                    instance: participant,
                    mutateFn: (p) => {
                      p.delete()
                    },
                  },
                  {
                    onSuccess: () => {
                      void queryClient.invalidateQueries({
                        queryKey: ['Task', 'search'],
                      })
                      void queryClient.invalidateQueries({
                        queryKey: ['Task', 'searchExtended'],
                      })
                    },
                  }
                )
              },
            }}
          />
        </>
      )}
    </MoreDropdownMenu>
  )
}
